import { useTranslation } from "@/i18n";
import { Check, Edit, Search } from "lucide-react";
import { useState } from "react";
import { TutorialContent } from "./components";

/**
 * Props for the LoadingStep component.
 */
interface LoadingStepProps {
  /** The current workflow step being processed */
  currentStep: "analyzing" | "processing";
}

/**
 * Type for the active content tab
 */
type ContentTab = "tutorials" | "flows";

/**
 * Configuration mapping step names to their display properties.
 */
const stepConfig = {
  analyzing: {
    Icon: Search,
    stepIndex: 0,
  },
  processing: {
    Icon: Edit,
    stepIndex: 1,
  },
} as const;

/**
 * Loading step component that shows progress through the workflow.
 * Displays a vertical list of steps with the current step highlighted and animated.
 * When on step 3 (analyzing), shows tutorial/flow toggle buttons and content area.
 *
 * @param props - Component props
 * @returns Loading indicator with step progression UI
 */
export default function LoadingStep({ currentStep }: LoadingStepProps) {
  const { t } = useTranslation();
  const currentStepIndex = stepConfig[currentStep]?.stepIndex ?? 0;
  const [activeTab, setActiveTab] = useState<ContentTab>("tutorials");

  const allSteps = [
    {
      title: t("documentUpload.analyzing.title"),
      Icon: Search,
    },
    {
      title: t("documentUpload.processing.title"),
      Icon: Edit,
    },
    {
      title: t("documentUpload.sidebar.completed"),
      Icon: Check,
    },
  ];

  return (
    <div className="flex flex-col items-center pt-8 gap-10">
      {/* Title */}
      <h2 className="text-xl font-semibold text-[#3f3f46] text-center">
        {t("documentUpload.analyzing.subtitle")}
      </h2>

      {/* Loading Steps */}
      <div className="flex flex-col gap-[15px] w-[282px]">
        {allSteps.map((step, index) => {
          const isActive = index === currentStepIndex;
          const isPast = index < currentStepIndex;
          const isFuture = index > currentStepIndex;
          const { Icon } = step;

          return (
            <div
              key={step.title}
              className={`flex items-center gap-[9px] px-[30px] ${
                isPast || isFuture ? "opacity-35" : "opacity-100"
              }`}
            >
              <div className="w-5 h-5 flex-shrink-0">
                <Icon
                  size={20}
                  className={`w-5 h-5 ${
                    isActive ? "text-[#18181b]" : "text-[#71717a]"
                  }`}
                />
              </div>
              <span
                className={`text-sm font-medium leading-[1.32] flex-1 font-sans ${
                  isActive ? "text-[#18181b] animate-pulse" : "text-[#71717a]"
                }`}
              >
                {step.title}
              </span>
            </div>
          );
        })}
      </div>
      {/* Tutorial/Flow Tabs and Content - Only shown when analyzing and language is German */}
      {
        <div className="flex flex-col gap-6 items-center">
          {/* Tab Buttons */}
          <div className="flex gap-4 items-center justify-center">
            <button
              onClick={() => setActiveTab("tutorials")}
              className={`flex gap-1 items-center justify-center px-3 py-1.5 rounded-lg border font-semibold text-sm transition-colors cursor-pointer ${
                activeTab === "tutorials"
                  ? "bg-[#f4eafd] border-[#e8d5fc] text-[#7512d6]"
                  : "bg-white border-[#e4e4e7] text-[#3f3f46] hover:bg-zinc-50"
              }`}
            >
              Fun with Tutorials
            </button>
            <button
              onClick={() => setActiveTab("flows")}
              className={`flex gap-1 items-center justify-center px-3 py-1.5 rounded-lg border font-semibold text-sm transition-colors cursor-pointer ${
                activeTab === "flows"
                  ? "bg-[#f4eafd] border-[#e8d5fc] text-[#7512d6]"
                  : "bg-white border-[#e4e4e7] text-[#3f3f46] hover:bg-zinc-50"
              }`}
            >
              Fun with Flows
            </button>
          </div>

          {/* Content Area */}
          <TutorialContent contentType={activeTab} />
        </div>
      }
    </div>
  );
}
